import React, { useState, useEffect } from 'react';
import adminAttendanceService from '../../firebase/adminAttendanceService';

/**
 * AttendanceFilters Component
 * Provides filtering controls for attendance records
 */
const AttendanceFilters = ({ filters, onFiltersChange, loading = false }) => {
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);

  // Load employees for filter dropdown
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setLoadingEmployees(true);
        const employeesList = await adminAttendanceService.getEmployeesForFilter();
        setEmployees(employeesList);
      } catch (error) {
        console.error('Error loading employees for filter:', error);
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, []);

  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      ...filters,
      [filterType]: value
    };
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      employeeId: 'all',
      status: 'all',
      type: 'all',
      location: 'all',
      validationScore: { min: 0, max: 100 }
    };
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = () => {
    return (
      filters.employeeId !== 'all' ||
      filters.status !== 'all' ||
      filters.type !== 'all' ||
      filters.location !== 'all' ||
      filters.validationScore?.min > 0 ||
      filters.validationScore?.max < 100
    );
  };

  return (
    <div className="attendance-filters">
      <div className="filters-header">
        <h3>Filters</h3>
        {hasActiveFilters() && (
          <button 
            className="clear-filters-btn"
            onClick={clearAllFilters}
            disabled={loading}
          >
            Clear All
          </button>
        )}
      </div>

      <div className="filters-grid">
        {/* Employee Filter */}
        <div className="filter-group">
          <label className="filter-label">Employee</label>
          <select
            className="filter-select"
            value={filters.employeeId || 'all'}
            onChange={(e) => handleFilterChange('employeeId', e.target.value)}
            disabled={loading || loadingEmployees}
          >
            <option value="all">All Employees</option>
            {employees.map((employee) => (
              <option key={employee.uid} value={employee.uid}>
                {employee.name} ({employee.employeeId})
              </option>
            ))}
          </select>
          {loadingEmployees && (
            <div className="filter-loading">Loading employees...</div>
          )}
        </div>

        {/* Status Filter */}
        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select
            className="filter-select"
            value={filters.status || 'all'}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            disabled={loading}
          >
            <option value="all">All Status</option>
            <option value="approved">✅ Approved</option>
            <option value="pending">⏳ Pending</option>
            <option value="rejected">❌ Rejected</option>
          </select>
        </div>

        {/* Type Filter */}
        <div className="filter-group">
          <label className="filter-label">Type</label>
          <select
            className="filter-select"
            value={filters.type || 'all'}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            disabled={loading}
          >
            <option value="all">All Types</option>
            <option value="clock-in">🟢 Clock In</option>
            <option value="clock-out">🔴 Clock Out</option>
          </select>
        </div>

        {/* Location Filter */}
        <div className="filter-group">
          <label className="filter-label">Location</label>
          <select
            className="filter-select"
            value={filters.location || 'all'}
            onChange={(e) => handleFilterChange('location', e.target.value)}
            disabled={loading}
          >
            <option value="all">All Locations</option>
            <option value="warehouse">🏢 Warehouse</option>
            <option value="jobsite">🌍 Job-site</option>
          </select>
        </div>

        {/* Validation Score Filter */}
        <div className="filter-group validation-score-filter">
          <label className="filter-label">
            Validation Score: {filters.validationScore?.min || 0}% - {filters.validationScore?.max || 100}%
          </label>
          <div className="range-inputs">
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={filters.validationScore?.min || 0}
              onChange={(e) => handleFilterChange('validationScore', {
                ...filters.validationScore,
                min: parseInt(e.target.value)
              })}
              disabled={loading}
              className="range-slider min-range"
            />
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={filters.validationScore?.max || 100}
              onChange={(e) => handleFilterChange('validationScore', {
                ...filters.validationScore,
                max: parseInt(e.target.value)
              })}
              disabled={loading}
              className="range-slider max-range"
            />
          </div>
          <div className="range-labels">
            <span>Min: {filters.validationScore?.min || 0}%</span>
            <span>Max: {filters.validationScore?.max || 100}%</span>
          </div>
        </div>

        {/* Search Filter */}
        <div className="filter-group search-filter">
          <label className="filter-label">Search</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search by employee name, notes..."
            value={filters.searchTerm || ''}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            disabled={loading}
          />
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters() && (
        <div className="active-filters">
          <div className="active-filters-header">Active Filters:</div>
          <div className="active-filters-list">
            {filters.employeeId !== 'all' && (
              <div className="active-filter-tag">
                <span>Employee: {employees.find(e => e.uid === filters.employeeId)?.name || 'Unknown'}</span>
                <button 
                  className="remove-filter-btn"
                  onClick={() => handleFilterChange('employeeId', 'all')}
                >
                  ×
                </button>
              </div>
            )}
            
            {filters.status !== 'all' && (
              <div className="active-filter-tag">
                <span>Status: {filters.status}</span>
                <button 
                  className="remove-filter-btn"
                  onClick={() => handleFilterChange('status', 'all')}
                >
                  ×
                </button>
              </div>
            )}
            
            {filters.type !== 'all' && (
              <div className="active-filter-tag">
                <span>Type: {filters.type}</span>
                <button 
                  className="remove-filter-btn"
                  onClick={() => handleFilterChange('type', 'all')}
                >
                  ×
                </button>
              </div>
            )}
            
            {filters.location !== 'all' && (
              <div className="active-filter-tag">
                <span>Location: {filters.location}</span>
                <button 
                  className="remove-filter-btn"
                  onClick={() => handleFilterChange('location', 'all')}
                >
                  ×
                </button>
              </div>
            )}
            
            {(filters.validationScore?.min > 0 || filters.validationScore?.max < 100) && (
              <div className="active-filter-tag">
                <span>Score: {filters.validationScore?.min || 0}%-{filters.validationScore?.max || 100}%</span>
                <button 
                  className="remove-filter-btn"
                  onClick={() => handleFilterChange('validationScore', { min: 0, max: 100 })}
                >
                  ×
                </button>
              </div>
            )}
            
            {filters.searchTerm && (
              <div className="active-filter-tag">
                <span>Search: "{filters.searchTerm}"</span>
                <button 
                  className="remove-filter-btn"
                  onClick={() => handleFilterChange('searchTerm', '')}
                >
                  ×
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Summary */}
      <div className="filter-summary">
        <div className="filter-summary-text">
          {hasActiveFilters() ? (
            <span>🔍 Filters applied - showing filtered results</span>
          ) : (
            <span>📋 Showing all attendance records</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceFilters;
